import { NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    OnDestroy,
    OnInit,
    Renderer2,
    signal,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25States } from "../../services/state/state.map";
import { Proto } from "../../pojo/Proto";
import { TaskService } from "../../services/task/task.service";
import { S25Util } from "../../util/s25-util";
import { StateService } from "../../services/state.service";
import { ContactService } from "../../services/contact.service";
import { NotificationService } from "../../services/notification.service";
import { S25Const } from "../../util/s25-const";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { InstCssService } from "../../services/intscss.service";
import { FlsService } from "../../services/fls.service";
import { AdmitModule } from "../admit/admit.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { S25MasqueradeModule } from "../S25-masquerade/masquerade.module";
import { PopoverModule } from "../s25-popover/popover.module";
import { S25ViewerMessageComponent } from "../s25-viewer-message/s25.viewer.message.component";
import { MoreMenuUtil } from "./more-menu/more.menu.util";
import { Debounce } from "../../decorators/debounce.decorator";
import { UserprefService } from "../../services/userpref.service";
import { hasReadFls } from "../../pojo/Fls";
import { StandoutClassroomService } from "../../services/standout.classroom.service";
import { Subscription } from "rxjs";
import { PermissionService } from "../../services/permission.service";
import { TelemetryService } from "../../services/telemetry.service";
import { LoginStore } from "../../stores/login.store";
import { s25Resource } from "../../util/s25.resource";
import { FlsStore } from "../../stores/fls.store";
import { S25MoreMenuComponent } from "./more-menu/s25.more.menu.component";
import { S25RouterLinkDirective } from "./s25.router.link.directive";

@TypeManagerDecorator("s25-ng-nav-bar")
@Component({
    selector: "s25-ng-nav-bar",
    template: `
        <div class="row">
            <a class="skip-main" href="javascript:void(0);" (click)="skipToMainContent()"
                >Skip to Content (Press Enter)</a
            >
            <div class="col-12" id="navbar-container" [class.logo-hidden]="!isVisible()">
                <div class="ngNav navbar">
                    <span class="c-navLogo">
                        <div class="layout">
                            <div class="header" role="banner">
                                <!-- this will be dynamical wrapped in a link if the preference has a value-->
                                <div class="logo" #logo role="img"></div>
                            </div>
                        </div>
                    </span>
                    <div class="navbar-links" role="navigation" aria-label="primary">
                        <ul class="nav navbar-nav">
                            @if (!StateMap.dash.isHidden && has25LiveAccess) {
                                <li class="c-mobileNav--hide">
                                    <a s25-ng-router-link [stateName]="StateNames.dash">
                                        <s25-ng-icon [type]="'schedulingAdvanced'" [ariaLabel]="false"></s25-ng-icon>
                                        {{ StateMap.dash.display }}
                                    </a>
                                </li>
                            }
                            @if (!StateMap.rose.isHidden && has25LiveAccess) {
                                <li class="c-nav-event">
                                    <a href="javascript:void(0)" (click)="goToRose()">
                                        <s25-ng-icon [type]="'navEdit'" [ariaLabel]="false"></s25-ng-icon>
                                        <span>{{ StateMap.rose.display }}</span>
                                    </a>
                                </li>
                            }
                            @if (hasTaskPermissions && has25LiveAccess) {
                                <li class="c-mobileNav--hide">
                                    <a
                                        s25-ng-router-link
                                        [stateName]="StateNames.search"
                                        [stateParams]="{
                                            viz: 'list',
                                            subject: 'task',
                                            search: taskDefault(),
                                        }"
                                        [options]="{ source: 'state-service' }"
                                        class="task-notifications"
                                    >
                                        <s25-ng-icon [type]="'task'" [ariaLabel]="false"></s25-ng-icon>
                                        <span class="text">Tasks</span>
                                        @if (tasksCount()) {
                                            <span class="count" [ngClass]="{ 'count--small': +tasksCount() < 9 }">
                                                {{ tasksCount() }}
                                            </span>
                                        }
                                    </a>
                                </li>
                            }
                            @if (this.hasChatPermissions) {
                                <li class="chat-link c-mobileNav--hide">
                                    <a
                                        href="javascript:void(0)"
                                        (click)="onChatLinkClick($event)"
                                        [attr.aria-label]="hasUnreadChats() ? 'Chat, unread' : null"
                                    >
                                        <s25-ng-icon
                                            [type]="'chat'"
                                            [ariaLabel]="false"
                                            class="chat-icon"
                                        ></s25-ng-icon>
                                        <span class="text">Chat</span>
                                        @if (hasUnreadChats()) {
                                            <span class="unread-dot" aria-hidden="true"></span>
                                        }
                                    </a>
                                </li>
                            }
                            <li>
                                <span class="c-nav-username">{{ styledName.value() }}</span>
                            </li>
                            @if (loginStore.loggedIn() && loginStore.isMasquerading()) {
                                <li>
                                    <button (click)="loginStore.logout()" class="c-textButton" id="masqueradeLogout">
                                        Exit Masquerade
                                    </button>
                                </li>
                            }
                            @if (!loginStore.loggedIn()) {
                                <li class="sign-in-link">
                                    <a href="javascript:void(0)" class="c-nav-signin" (click)="loginStore.login()"
                                        >Sign In</a
                                    >
                                </li>
                            }
                        </ul>
                        <s25-ng-more-menu
                            data-testid="moreMenu"
                            [styledName]="styledName.value() ?? 'Unknown'"
                            [tasksCount]="tasksCount()"
                            [taskDefault]="taskDefault()"
                            [hasUnreadChats]="hasUnreadChats()"
                            (logIn)="loginStore.login()"
                            (logOut)="loginStore.logout()"
                            (chatClick)="onChatLinkClick(undefined)"
                        ></s25-ng-more-menu>
                    </div>
                </div>
            </div>
        </div>
        <s25-ng-viewer-message></s25-ng-viewer-message>
    `,
    styles: `
        .task-notifications {
            display: flex;
            gap: 3px;
            align-items: center;
        }

        .navbar-nav {
            --color--link-text: var(--color-navbar-link);
            --color--link-text-hover: var(--color-navbar-link-hover);
        }
        .navbar-links {
            height: 100%;
        }

        s25-ng-more-menu {
            height: 100%;
            display: flex;
            align-items: center;
        }

        .chat-link a {
            position: relative; /* anchor the dot to the link */
        }

        .chat-icon {
            margin-right: 2px;
        }

        .unread-dot {
            position: absolute;
            width: 8px;
            height: 8px;
            background-color: red;
            border-radius: 50%;
        }
    `,
    imports: [
        S25LoadingInlineModule,
        PopoverModule,
        FormsModule,
        S25IconComponent,
        S25ViewerMessageComponent,
        S25RouterLinkDirective,
        S25MasqueradeModule,
        AdmitModule,
        NgClass,
        S25MoreMenuComponent,
    ],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25NavBarComponent implements OnInit, OnDestroy {
    protected readonly StateMap = S25States.untyped;
    protected readonly StateNames = S25States.names;

    @ViewChild("logo", { static: false }) logoElement: ElementRef;
    readonly loginStore = inject(LoginStore);
    readonly fls = inject(FlsStore);

    logoLink: Proto.URLString = "";
    taskDefault = signal<string | undefined>(undefined); //Default task search

    tasksCount = signal<string>("");
    hasTaskPermissions: boolean;
    hasChatPermissions: boolean;
    has25LiveAccess: boolean = true; // Default to true, will be updated in getSecondaryData
    hasUnreadChats = signal(false);
    unreadChatsSubscription: Subscription;
    private windowFocusListener?: () => void;

    // Scroll behavior signals - nav bar hides when scrolling up, shows when scrolling down in mobile app
    private scrollY = signal(0);
    private lastScrollY = signal(0);
    private scrollDirection = signal<"up" | "down">("up");
    private isScrolling = signal(false);

    // Computed signals for navbar behavior
    isAtTop = computed(() => this.scrollY() < 5);
    isVisible = computed(() => {
        // Always show when at top, or when scrolling up (but not actively scrolling down)
        return this.isAtTop() || (this.scrollDirection() === "up" && !this.isScrolling());
    });

    readonly styledName = s25Resource({
        defaultValue: "Guest",
        params: () => this.loginStore.user(),
        loader: async ({ params, abortSignal }) => {
            return ContactService.getCurrentNameStyled();
        },
    });
    readonly masquerade = computed(() => {
        if (this.loginStore.user() && this.loginStore.isMasquerading()) {
            return `Masquerading as ${this.styledName.value()}`;
        }
    });

    // Scroll handling properties
    private ticking = false;
    private scrollTimeout: NodeJS.Timeout;

    @HostListener("window:scroll")
    @HostListener("document:scroll")
    onWindowScroll() {
        if (!S25Const.inApp) return;
        this.handleScroll();
    }

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private renderer: Renderer2,
    ) {
        this.elementRef.nativeElement.angBridge = this;

        effect((onCleanup) => {
            if (this.masquerade()) {
                const masqueradeBanner = this.displayMasqueradeBanner();
                onCleanup(() => masqueradeBanner.remove());
            }
        });
    }

    async ngOnInit() {
        InstCssService.setInstCss(); //note: also sets up inst css colors, logo, etc

        if (S25Const.inApp) {
            // detect scroll for navbar show/hide in mobile app
            this.updateScrollState();
            // once nav bar is loaded, hide loading view
            document.dispatchEvent(new Event("s25-app-ready"));
        }

        await this.getSecondaryData();
        this.updateTasksCount();
        this.cd.detectChanges();
    }

    private handleScroll() {
        if (!this.ticking) {
            requestAnimationFrame(() => this.updateScrollState());
            this.ticking = true;
        }
    }

    private updateScrollState() {
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        // Set scrolling state
        this.isScrolling.set(true);
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
        this.scrollTimeout = setTimeout(() => {
            this.isScrolling.set(false);
        }, 150);

        // Update scroll position
        this.scrollY.set(currentScrollY);

        // Determine scroll direction with threshold to prevent jitter
        if (Math.abs(currentScrollY - this.lastScrollY()) > 3) {
            this.scrollDirection.set(currentScrollY > this.lastScrollY() ? "down" : "up");
            this.lastScrollY.set(currentScrollY);
        }

        this.ticking = false;
    }

    /**
     * data that can be loaded in the background
     */
    async getSecondaryData() {
        const prefsP = this.getPreferences();
        const flsP = FlsService.getFls();
        const groupP = UserprefService.getGroupId();
        await prefsP;
        const fls = await flsP;
        const groupId = await groupP;
        const has25LiveAccessP = PermissionService.has25LiveAccess();

        this.hasTaskPermissions = this.loginStore.loggedIn() && fls.TASK_LIST !== "N";
        this.hasChatPermissions =
            this.loginStore.loggedIn() && S25Const.socEnabled && (groupId === -1 || hasReadFls(fls?.CHAT));
        if (this.hasChatPermissions) {
            void this.checkUnreadChats();
        }
        this.has25LiveAccess = await has25LiveAccessP;
    }

    public async checkUnreadChats() {
        if (!this.hasChatPermissions) return;

        // Start the recurring check and get initial data
        try {
            const unreadChats = await StandoutClassroomService.getUnreadChatsWithRecurringCheck();
            this.hasUnreadChats.set(unreadChats.length > 0);
        } catch (error) {
            console.error("Error checking unread chats:", error);
            this.hasUnreadChats.set(false);
        }

        // Subscribe to updates from the service
        this.unreadChatsSubscription?.unsubscribe();
        this.unreadChatsSubscription = StandoutClassroomService.getUnreadChatsObservable().subscribe((unreadChats) => {
            this.hasUnreadChats.set(unreadChats.length > 0);
            this.cd.detectChanges();
        });

        // Refresh unread chats when window regains focus
        if (this.windowFocusListener) {
            window.removeEventListener("focus", this.windowFocusListener);
        }
        this.windowFocusListener = (): void => void StandoutClassroomService.forceRefreshUnreadChats();
        window.addEventListener("focus", this.windowFocusListener);
    }

    @Debounce(300)
    @HostListener("window:updateTaskCount")
    async updateTasksCount() {
        if (!this.loginStore.loggedIn()) return;
        if (!this.fls.hasFull("TASK_LIST")) return;

        const [numTasks, error] = await S25Util.Maybe(TaskService.getHeaderTaskCount());
        if (numTasks > 0) this.tasksCount.set(numTasks.toString());
        else this.tasksCount.set("");

        this.cd.detectChanges();
    }

    displayMasqueradeBanner() {
        const banner = NotificationService.postBarNotification(this.masquerade(), false, true, false, null, "Exit");

        const navbar = this.elementRef.nativeElement.querySelector(".navbar") as HTMLElement;
        const bar = this.elementRef.nativeElement.querySelector(".notification-bar") as HTMLElement;

        if (navbar) {
            this.renderer.setStyle(navbar, "marginBottom", "unset");
        }

        if (bar) {
            this.renderer.setStyle(bar, "marginBottom", "1em");

            if (banner.isSticky) {
                this.renderer.setStyle(bar, "position", "sticky");
            }
        }

        return banner;
    }

    skipToMainContent() {
        document.getElementById("mainContent")?.focus();
    }

    goToRose() {
        StateService.gotoRose();
    }

    async onChatLinkClick(event: Event | undefined) {
        event?.preventDefault();
        TelemetryService.send("Chat", "Chat_TopNav");
        await StateService.go(S25States.names.chat, {}, {});
    }

    /**
     * Wraps the logo element in a link
     */
    wrapLogoInLink() {
        if (this.logoLink && this.logoElement?.nativeElement) {
            const logo = this.logoElement.nativeElement;

            // Create an anchor tag
            const anchor = this.renderer.createElement("a");
            this.renderer.setAttribute(anchor, "href", this.logoLink);
            this.renderer.setAttribute(anchor, "target", "_blank");
            this.renderer.setAttribute(anchor, "rel", "noopener noreferrer");
            this.renderer.setAttribute(anchor, "class", "ngLogoLink");
            this.renderer.setAttribute(anchor, "aria-label", "Link to Home Page");

            // Move the logo element inside the anchor tag
            const parent = logo.parentNode;
            this.renderer.appendChild(anchor, logo);
            this.renderer.appendChild(parent, anchor);
            this.cd.detectChanges();
        }
    }

    async getPreferences() {
        const prefs = await MoreMenuUtil.getPreferences();
        this.logoLink = prefs?.config_logo_link?.value;

        MoreMenuUtil.applyDarkModeFromPreferenceValue(prefs?.pro_dark_mode?.value);
        this.wrapLogoInLink();

        const taskString = prefs?.TaskHeaderCount?.value || "Outstanding";
        const taskQueryId = taskString.match(/\d+/);
        this.taskDefault.set(taskQueryId ? taskQueryId[0] : taskString);
    }

    ngOnDestroy() {
        this.unreadChatsSubscription?.unsubscribe();
        if (this.windowFocusListener) {
            window.removeEventListener("focus", this.windowFocusListener);
        }
    }
}
